import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faSearch, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { WeeklyReport } from "components/pages/Educator/WeeklyReport/WeeklyReport";

export const AdminWeeklyReport = ({ selectedEducator: externalEducator, onEducatorChange, sharedClassId, onClassChange }) => {
  const [internalEducator, setInternalEducator] = useState(null);
  const selectedEducator = externalEducator !== undefined ? externalEducator : internalEducator;
  const setSelectedEducator = onEducatorChange || setInternalEducator;
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const params = new URLSearchParams();
        params.append("role", "educator");
        params.append("search", searchTerm.trim());
        params.append("limit", "10");
        const response = await request(`admin/users?${params.toString()}`, { method: "GET" });
        setSearchResults(response.users || []);
      } catch (e) {
        setSearchError("Failed to search educators.");
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  if (selectedEducator) {
    const isControlled = externalEducator !== undefined;
    return (
      <div>
        {!isControlled && (
          <EducatorBanner>
            <BackButton onClick={() => setSelectedEducator(null)}>
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Change Educator</span>
            </BackButton>
            <EducatorInfo>
              Viewing reports for: <strong>{selectedEducator.name}</strong>
              {selectedEducator.email && <span> ({selectedEducator.email})</span>}
            </EducatorInfo>
          </EducatorBanner>
        )}
        <WeeklyReport key={selectedEducator.id} educatorId={selectedEducator.id} sharedClassId={sharedClassId} onClassChange={onClassChange} />
      </div>
    );
  }

  return (
    <SearchContainer>
      <SearchHeader>Select an Educator</SearchHeader>
      <SearchInputWrapper>
        <SearchIcon>
          <FontAwesomeIcon icon={faSearch} />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
      </SearchInputWrapper>

      {searchLoading && (
        <LoadingState>
          <FontAwesomeIcon icon={faSpinner} spin color={colors.blue500} />
        </LoadingState>
      )}

      {searchError && <ErrorState>{searchError}</ErrorState>}

      {!searchLoading && searchResults.length > 0 && (
        <ResultsTable>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {searchResults.map((user) => (
              <Tr key={user.id} onClick={() => setSelectedEducator({
                id: user.id,
                name: user.name || user.display_name || user.email,
                email: user.email,
              })}>
                <Td>{user.name || user.display_name || "—"}</Td>
                <Td>{user.email || "—"}</Td>
                <Td>
                  <SelectButton>View Reports</SelectButton>
                </Td>
              </Tr>
            ))}
          </tbody>
        </ResultsTable>
      )}

      {!searchLoading && searchTerm.trim() && searchResults.length === 0 && !searchError && (
        <EmptyState>No educators found for "{searchTerm}"</EmptyState>
      )}
    </SearchContainer>
  );
};

const SearchContainer = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SearchHeader = styled.h2`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  max-width: 480px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.milkGlass800};
  font-size: 14px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${colors.blue500};
    box-shadow: 0 0 0 2px ${colors.blue100 || "rgba(59, 130, 246, 0.1)"};
  }
`;

const LoadingState = styled.div`
  padding: 24px;
  text-align: center;
`;

const ErrorState = styled.div`
  color: ${colors.orange600};
  font-size: 14px;
`;

const EmptyState = styled.div`
  color: ${colors.milkGlass950};
  font-size: 14px;
  padding: 16px 0;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0px 1px 3px 0px rgba(14, 26, 49, 0.1), 0px 1px 2px 0px rgba(14, 26, 49, 0.06);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.milkGlass800};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.milkGlass300 || "#e5e7eb"};
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background-color: ${colors.milkGlass200};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  border-bottom: 1px solid ${colors.milkGlass300 || "#e5e7eb"};
`;

const SelectButton = styled.span`
  color: ${colors.blue500};
  font-weight: 500;
  font-size: 14px;
`;

const EducatorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 32px;
  background-color: ${colors.blue50};
  border-bottom: 1px solid ${colors.blue200 || "#bfdbfe"};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${colors.blue500};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background-color: ${colors.blue100 || "rgba(59, 130, 246, 0.1)"};
  }
`;

const EducatorInfo = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
`;
